import * as React from 'react';

// mobx
import { observer } from 'mobx-react';

// Hooks
import { useStores } from 'hooks';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'hooks';

import { UiStore } from 'stores';
import { UitkTertiaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { PositionType, UitkMenu, UitkMenuContainer, UitkMenuList, UitkMenuTrigger } from 'uitk-react-menu';
import { UitkText } from 'uitk-react-text';
import { analyticsEvent, getUserAnalyticsMicroMessage } from '../../util/analytics';

export const LanguageSelectorV2: React.FunctionComponent = observer(() => {
  const location = useLocation();

  const { context, analytics, uiStore, pzStore, userAuth } = useStores();
  const { supportedLocales } = uiStore;
  const currentLang =
    supportedLocales &&
    supportedLocales.find((locale) => {
      return locale.langId === context.langId;
    });

  React.useEffect(() => {
    const storedLocale = localStorage.getItem('locale');
    if (!storedLocale || context.locale !== storedLocale) {
      localStorage.setItem('locale', context.locale);
    }
  }, []);

  // Set state
  const [activeLabel, setActiveLabel] = React.useState(currentLang.langLabel);
  const [open, setOpen] = React.useState(false);

  let pathWithoutLocale: string;
  let path: string;

  // Do this for every page other than the first homepage load
  if (location.pathname !== '/home') {
    // Get path from the route, strip out locale
    const pathArray = location.pathname.split('/');
    const param = location.search;

    if (pathArray) {
      // take out first pieces of URL '' and '/xx-xx/'
      pathArray.splice(0, 2);
      pathWithoutLocale = pathArray && pathArray.join('/');
    }

    path = param ? pathWithoutLocale + param : pathWithoutLocale;
    // Homepage starts without locale prefix so don't try remove
  } else {
    path = 'home';
  }

  const langList =
    supportedLocales &&
    supportedLocales.map((lang, i) => {
      return {
        attributes: {
          role: 'menuitem',
          tabindex: i + 1,
        },
        label: lang.langLabel,
        className: 'uitk-list-item ' + (context.langId === lang.langId ? 'langSelected' : ''),
        href: `/${lang.langCode}/${path}`,
        typeAnchor: true,
        onClick: (e) => handleItemClick(e, lang.langLabel),
        analytics: {
          id: 'affhub_language_switcher.submitted',
          callback() {
            analyticsTrackEvents('affhub_language_switcher.submitted');
          },
        },
      };
    });

  function handleItemClick(e, label) {
    setActiveLabel(label);
    setOpen(false);
  }

  function handleTriggerClicked(e) {
    setOpen(!open);
  }

  const analyticsTrackEvents = (rfrr: string) => {
    const linkname = {
      'affhub_language_switcher.selected': 'Affhub Language Selected',
      'affhub_language_switcher.submitted': 'Affhub Language Switched',
    }[rfrr];

    analyticsEvent(analytics, userAuth, pzStore, rfrr, linkname);
  };

  const trackMenuOpen = {
    id: 'affhub_language_switcher.selected',
    callback() {
      analyticsTrackEvents('affhub_language_switcher.selected');
    },
  };

  const windowSize = useWindowSize();
  const EXTRA_LARGE_SIZE = 1199;

  return (
    <UitkMenu isOpen={open} onTriggerClick={(e) => handleTriggerClicked(e)}>
      <UitkMenuTrigger>
        <UitkTertiaryButton
          inverse={true}
          analytics={trackMenuOpen}
          size={windowSize.width > EXTRA_LARGE_SIZE ? UitkButtonSize.SMALL : UitkButtonSize.MEDIUM}
        >
          <div>
            <UitkIcon name="language" className="Language__icon" />
            <UitkIcon name="arrow_drop_down" className="Language__icon" />
          </div>
        </UitkTertiaryButton>
      </UitkMenuTrigger>
      <UitkMenuContainer autoPosition={true}>
        <UitkMenuList items={langList} />
      </UitkMenuContainer>
    </UitkMenu>
  );
});
